import React, { useEffect, useState } from "react";
import { config } from "~/core";
import { apiAutoSchedule } from "../api/autoschedule";
import { toast } from "react-toastify";

// Auto-Schedule tuning config (FOU-625). Superuser-only. Edits the params the
// dry-run auto-scheduler uses: lookahead range, max fullness, max stops per
// route, marginal drive-cost threshold (fresh/stale), pilot work area(s),
// daily batch cap, and the "only join existing routes" toggle (ON-only for now).
function AutoScheduleConfig() {
  const [cfg, setCfg] = useState(null);
  const [areasText, setAreasText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiAutoSchedule
      .getConfig()
      .then((res) => {
        if (res.data && res.data.status === 200 && res.data.result) {
          setCfg(res.data.result);
          setAreasText((res.data.result.pilotWorkAreaIds || []).join(", "));
        }
      })
      .catch((err) => toast.error("Failed to load config"));
  }, []);

  if (!config.isSuperAdmin) {
    return (
      <div className="tw-p-6 tw-text-sm tw-text-gray-600">
        You need superuser access to view auto-schedule config.
      </div>
    );
  }

  if (!cfg) {
    return <div className="tw-p-6 tw-text-sm tw-text-gray-500">Loading config…</div>;
  }

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = () => {
    // Parse the comma-separated work areas into ints.
    const ids = areasText
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    const body = { ...cfg, pilotWorkAreaIds: ids };
    setSaving(true);
    apiAutoSchedule
      .saveConfig(body)
      .then((res) => {
        if (res.data && res.data.status === 200) {
          toast.success("Auto-schedule config saved");
        } else {
          toast.error("Failed to save config");
        }
      })
      .catch(() => toast.error("Failed to save config"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="tw-p-6 tw-max-w-2xl">
      <div className="tw-mb-4">
        <h1 className="tw-text-xl tw-font-semibold tw-text-gray-800">Auto-Schedule Config</h1>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500">
          Tuning parameters for the auto-scheduler (dry-run). Changes apply within ~1 minute (cached).
        </p>
      </div>

      <div className="tw-space-y-4">
        <Field label="Lookahead min (days)" hint="Earliest day to offer (from today).">
          <NumInput value={cfg.lookaheadMin} isInt onChange={(v) => set("lookaheadMin", v)} />
        </Field>
        <Field label="Lookahead max (days)" hint="Latest day to search.">
          <NumInput value={cfg.lookaheadMax} isInt onChange={(v) => set("lookaheadMax", v)} />
        </Field>
        <Field label="Max route fullness %" hint="Max total route fullness (with candidate) to offer.">
          <NumInput value={cfg.fullnessCapPct} step={5} onChange={(v) => set("fullnessCapPct", v)} />
        </Field>
        <Field label="Max stops per route" hint="Max inspections on a route. 0 = no cap.">
          <NumInput value={cfg.maxStopsPerRoute} isInt onChange={(v) => set("maxStopsPerRoute", v)} />
        </Field>
        <Field label="Marginal threshold — fresh (min)" hint="Max extra driving a fresh inspection can add to a route.">
          <NumInput value={cfg.marginalThresholdMin} step={5} onChange={(v) => set("marginalThresholdMin", v)} />
        </Field>
        <Field label="Marginal threshold — stale (min)" hint="Max extra driving a stale (at-deadline) inspection can add. Slides between fresh and stale by age.">
          <NumInput value={cfg.marginalThresholdMax} step={5} onChange={(v) => set("marginalThresholdMax", v)} />
        </Field>
        <Field label="Pilot work area IDs" hint="Comma-separated work area ids to target.">
          <input
            type="text"
            value={areasText}
            onChange={(e) => setAreasText(e.target.value)}
            className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm"
            placeholder="e.g. 2"
          />
        </Field>
        <Field label="Daily batch cap" hint="Max candidates the 6:30am sweep enqueues per run.">
          <NumInput value={cfg.batchCap} step={50} isInt onChange={(v) => set("batchCap", v)} />
        </Field>
        <Field label="Fairness weight (0–1)" hint="0 = pure marginal (throughput, least driving). 1 = pure fullness (fairness, balance per-job load). 0.5 = balanced.">
          <NumInput value={cfg.fairnessWeight} step={0.1} onChange={(v) => set("fairnessWeight", v)} />
        </Field>
        <Field label="Ferry detour threshold (min)" hint="Max extra journey time before the no-ferry routing falls back to allowing ferries (island / big-detour case). Default 60 (1 hour). Applies to all VROOM routes.">
          <NumInput value={cfg.ferryDetourThresholdMin} step={5} isInt onChange={(v) => set("ferryDetourThresholdMin", v)} />
        </Field>
        <Field label="Only join existing routes" hint="ON-only for now — the 'allow new days' capacity model isn't built yet.">
          <label className="tw-flex tw-items-center tw-gap-2">
            <input type="checkbox" checked={cfg.onlyJoinExistingRoutes} disabled className="tw-w-5 tw-h-5" />
            <span className="tw-text-sm tw-text-gray-500">Locked ON</span>
          </label>
        </Field>
      </div>

      <div className="tw-mt-6">
        <button
          onClick={save}
          disabled={saving}
          className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded tw-text-sm tw-font-medium disabled:tw-opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">{label}</label>
      <div className="tw-mt-1">{children}</div>
      {hint && <div className="tw-mt-1 tw-text-xs tw-text-gray-500">{hint}</div>}
    </div>
  );
}

function NumInput({ value, onChange, step, isInt }) {
  const parse = isInt ? (s) => parseInt(s, 10) || 0 : (s) => parseFloat(s) || 0;
  return (
    <input
      type="number"
      value={value}
      step={step || 1}
      onChange={(e) => onChange(parse(e.target.value))}
      className="tw-w-40 tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm"
    />
  );
}

export default AutoScheduleConfig;